import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { IMAGES, bgStyle } from '@/config/images';
import { 
  Home, 
  Calculator, 
  Settings2, 
  Wrench, 
  AlertTriangle, 
  ArrowRight,
  CheckCircle,
  Zap,
  Target,
  RefreshCw,
  ChevronRight
} from 'lucide-react';

const Introduction = () => {
  const navigate = useNavigate();

  const handleProceed = () => {
    navigate('/auth');
  };

  return (
    <div 
      data-testid="introduction-page" 
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6"
      style={bgStyle(IMAGES.GETTING_STARTED)}
    >
      {/* Dark Overlay */}
      <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />
      
      <div className="max-w-4xl mx-auto space-y-8 pb-12">
      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          CNC-Helpdesk
        </h1>
        <p className="text-xl md:text-2xl text-[#e67e22] font-semibold">
          Get your CNC dialed in – fast, safe, repeatable.
        </p>
      </div>

      {/* Intro Description */}
      <div className="bg-[#2c2c2c]/80 border border-[#3a3a3a] rounded-sm p-6 space-y-4">
        <p className="text-base md:text-lg text-[#d6d6d6] leading-relaxed">
          CNC-Helpdesk is your control centre for feeds & speeds, machine setup, troubleshooting, and CAM-ready tool exports.
        </p>
        <p className="text-base md:text-lg text-[#a3a3a3] leading-relaxed">
          This isn't theory or forum noise – it's a <span className="text-[#e67e22] font-semibold">simple workflow</span> you can follow every time you cut.
        </p>
      </div>

      {/* How to Get Set Up */}
      <div className="space-y-6">
        <h2 className="text-2xl md:text-3xl font-bold text-white uppercase tracking-wide flex items-center gap-3" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          <Target className="w-7 h-7 text-[#e67e22]" />
          How to Get Set Up (Step by Step)
        </h2>

        {/* Step 1 - Dashboard */}
        <div className="bg-[#242424] border border-[#3a3a3a] rounded-sm p-5 space-y-4 hover:border-[#e67e22]/30 transition-colors">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#e67e22] rounded-full flex items-center justify-center text-white font-bold text-lg">1</div>
            <h3 className="text-lg md:text-xl font-bold text-white">Start on the Dashboard</h3>
          </div>
          <p className="text-[#a3a3a3] ml-13">This is your home base.</p>
          <p className="text-[#d6d6d6] ml-13">From here you can jump to:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 ml-13">
            <Link to="/calculator" className="flex items-center gap-2 text-sm hover:bg-[#2c2c2c] p-1 rounded transition-colors">
              <Calculator className="w-4 h-4 text-[#e67e22]" />
              <span className="text-[#e67e22] font-semibold hover:underline">Calculator</span>
              <span className="text-[#a3a3a3]">– get feeds, speeds, depths</span>
            </Link>
            <Link to="/settings/machine-setup" className="flex items-center gap-2 text-sm hover:bg-[#2c2c2c] p-1 rounded transition-colors">
              <Settings2 className="w-4 h-4 text-[#e67e22]" />
              <span className="text-[#e67e22] font-semibold hover:underline">My Equipment</span>
              <span className="text-[#a3a3a3]">– your machine</span>
            </Link>
            <Link to="/settings/machine-setup?tab=tools" className="flex items-center gap-2 text-sm hover:bg-[#2c2c2c] p-1 rounded transition-colors">
              <Wrench className="w-4 h-4 text-[#e67e22]" />
              <span className="text-[#e67e22] font-semibold hover:underline">My Tools</span>
              <span className="text-[#a3a3a3]">– manage tool library</span>
            </Link>
            <Link to="/troubleshooting" className="flex items-center gap-2 text-sm hover:bg-[#2c2c2c] p-1 rounded transition-colors">
              <AlertTriangle className="w-4 h-4 text-[#e67e22]" />
              <span className="text-[#e67e22] font-semibold hover:underline">Troubleshooting</span>
              <span className="text-[#a3a3a3]">– fix bad cuts</span>
            </Link>
          </div>
          <p className="text-[#d6d6d6] ml-13 text-sm italic border-l-2 border-[#e67e22] pl-3 mt-3">
            If you're new, follow the steps below in order.
          </p>
        </div>

        {/* Step 2 - Profile & Units */}
        <div className="bg-[#242424] border border-[#3a3a3a] rounded-sm p-5 space-y-4 hover:border-[#e67e22]/30 transition-colors">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#e67e22] rounded-full flex items-center justify-center text-white font-bold text-lg">2</div>
            <h3 className="text-lg md:text-xl font-bold text-white">Set Your Basics (Profile & Units)</h3>
          </div>
          <p className="text-[#a3a3a3] ml-13">Before anything else, make sure the app speaks your language:</p>
          <ul className="ml-13 space-y-2">
            <li className="flex items-center gap-2 text-[#d6d6d6]">
              <CheckCircle className="w-4 h-4 text-[#27ae60]" />
              Choose <span className="text-[#e67e22] font-semibold">mm</span> or <span className="text-[#e67e22] font-semibold">inch</span>
            </li>
            <li className="flex items-center gap-2 text-[#d6d6d6]">
              <CheckCircle className="w-4 h-4 text-[#27ae60]" />
              Pick your experience level: <span className="text-[#e67e22] font-semibold">Beginner / Intermediate / Advanced</span>
            </li>
          </ul>
          <div className="ml-13 bg-[#1a1a1a] border border-[#3a3a3a] rounded-sm p-3 text-sm">
            <p className="text-[#a3a3a3]"><span className="text-[#e67e22]">Beginner</span> = more conservative, safer suggestions.</p>
            <p className="text-[#a3a3a3]"><span className="text-[#e67e22]">Advanced</span> = more control, still with sane limits.</p>
          </div>
          <p className="text-[#d6d6d6] ml-13 text-sm">These settings become your defaults across the app.</p>
        </div>

        {/* Step 3 - My Equipment */}
        <div className="bg-[#242424] border border-[#3a3a3a] rounded-sm p-5 space-y-4 hover:border-[#e67e22]/30 transition-colors">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#e67e22] rounded-full flex items-center justify-center text-white font-bold text-lg">3</div>
            <h3 className="text-lg md:text-xl font-bold text-white">Tell the App About Your Machine (My Equipment)</h3>
          </div>
          <p className="text-[#a3a3a3] ml-13">Go to <Link to="/settings/machine-setup" className="text-[#e67e22] font-semibold hover:underline">My Equipment</Link> and add your main machine:</p>
          <ul className="ml-13 space-y-2">
            <li className="flex items-start gap-2 text-[#d6d6d6]">
              <ChevronRight className="w-4 h-4 text-[#e67e22] mt-0.5" />
              Select <span className="text-[#e67e22]">Machine class</span>: Hobby/Light, Medium, or Heavy/Rigid
            </li>
            <li className="flex items-start gap-2 text-[#d6d6d6]">
              <ChevronRight className="w-4 h-4 text-[#e67e22] mt-0.5" />
              Pick <span className="text-[#e67e22]">Spindle type</span>: Trim router or VFD spindle
            </li>
            <li className="flex items-start gap-2 text-[#d6d6d6]">
              <ChevronRight className="w-4 h-4 text-[#e67e22] mt-0.5" />
              Enter your <span className="text-[#e67e22]">max RPM</span>
            </li>
            <li className="flex items-start gap-2 text-[#737373]">
              <ChevronRight className="w-4 h-4 text-[#737373] mt-0.5" />
              (Optional) Choose the materials you cut most
            </li>
          </ul>
          <div className="ml-13 bg-[#1a1a1a] border border-[#e67e22]/30 rounded-sm p-4 mt-3">
            <p className="text-[#d6d6d6] font-semibold mb-2">CNC-Helpdesk uses this profile to:</p>
            <ul className="space-y-1 text-sm text-[#a3a3a3]">
              <li className="flex items-center gap-2"><Zap className="w-3 h-3 text-[#e67e22]" /> Derate routers vs spindles</li>
              <li className="flex items-center gap-2"><Zap className="w-3 h-3 text-[#e67e22]" /> Respect your rigidity</li>
              <li className="flex items-center gap-2"><Zap className="w-3 h-3 text-[#e67e22]" /> Keep recommendations realistic for your machine</li>
            </ul>
          </div>
        </div>

        {/* Step 4 - My Tools */}
        <div className="bg-[#242424] border border-[#3a3a3a] rounded-sm p-5 space-y-4 hover:border-[#e67e22]/30 transition-colors">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#e67e22] rounded-full flex items-center justify-center text-white font-bold text-lg">4</div>
            <h3 className="text-lg md:text-xl font-bold text-white">Set Up Your Tools (My Tools)</h3>
          </div>
          <p className="text-[#a3a3a3] ml-13">Next, open <Link to="/settings/machine-setup?tab=tools" className="text-[#e67e22] font-semibold hover:underline">My Tools</Link>.</p>
          <p className="text-[#d6d6d6] ml-13">You can:</p>
          <ul className="ml-13 space-y-2">
            <li className="flex items-start gap-2 text-[#d6d6d6]">
              <CheckCircle className="w-4 h-4 text-[#27ae60] mt-0.5" />
              Use a <span className="text-[#e67e22]">starter tool set</span> (common bits ready to go)
            </li>
            <li className="flex items-start gap-2 text-[#d6d6d6]">
              <CheckCircle className="w-4 h-4 text-[#27ae60] mt-0.5" />
              <span className="text-[#e67e22]">Add tools manually</span> (diameter, flutes, typical RPM)
            </li>
            <li className="flex items-start gap-2 text-[#d6d6d6]">
              <CheckCircle className="w-4 h-4 text-[#27ae60] mt-0.5" />
              <span className="text-[#e67e22]">Import from Vectric Aspire / VCarve / other CAM</span>
            </li>
          </ul>
          <div className="ml-13 bg-[#1a1a1a] border border-[#3a3a3a] rounded-sm p-4 mt-3">
            <p className="text-[#d6d6d6] font-semibold mb-2">Once this is done:</p>
            <ul className="space-y-1 text-sm text-[#a3a3a3]">
              <li>• The <Link to="/calculator" className="text-[#e67e22] hover:underline">Calculator</Link> shows your tools in the dropdown</li>
              <li>• Exports generate tool files that match your CAM setup</li>
              <li>• <span className="text-[#27ae60]">No more double-entering tool data</span></li>
            </ul>
          </div>
        </div>

        {/* Step 5 - Calculator */}
        <div className="bg-[#242424] border border-[#3a3a3a] rounded-sm p-5 space-y-4 hover:border-[#e67e22]/30 transition-colors">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#e67e22] rounded-full flex items-center justify-center text-white font-bold text-lg">5</div>
            <h3 className="text-lg md:text-xl font-bold text-white">Run Your First Calculation (Calculator)</h3>
          </div>
          <p className="text-[#e67e22] ml-13 font-semibold">Now the fun part.</p>
          <p className="text-[#a3a3a3] ml-13">Open the <Link to="/calculator" className="text-[#e67e22] font-semibold hover:underline">Calculator</Link> and:</p>
          <ul className="ml-13 space-y-2">
            <li className="flex items-start gap-2 text-[#d6d6d6]">
              <ChevronRight className="w-4 h-4 text-[#e67e22] mt-0.5" />
              Pick your <span className="text-[#e67e22]">material</span> (e.g. Pine, MDF, Hardwood)
            </li>
            <li className="flex items-start gap-2 text-[#d6d6d6]">
              <ChevronRight className="w-4 h-4 text-[#e67e22] mt-0.5" />
              Choose a <span className="text-[#e67e22]">tool</span> from your library
            </li>
            <li className="flex items-start gap-2 text-[#d6d6d6]">
              <ChevronRight className="w-4 h-4 text-[#e67e22] mt-0.5" />
              Select a <span className="text-[#e67e22]">strategy</span> (profile, pocket, surfacing, etc.)
            </li>
          </ul>
          <p className="text-[#d6d6d6] ml-13 mt-3">Review the suggested:</p>
          <div className="ml-13 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
            <div className="bg-[#1a1a1a] border border-[#3a3a3a] rounded-sm py-2 px-3">
              <span className="text-[#e67e22] font-semibold text-sm">Feed rate</span>
            </div>
            <div className="bg-[#1a1a1a] border border-[#3a3a3a] rounded-sm py-2 px-3">
              <span className="text-[#e67e22] font-semibold text-sm">Spindle speed</span>
            </div>
            <div className="bg-[#1a1a1a] border border-[#3a3a3a] rounded-sm py-2 px-3">
              <span className="text-[#e67e22] font-semibold text-sm">Step-down</span>
            </div>
            <div className="bg-[#1a1a1a] border border-[#3a3a3a] rounded-sm py-2 px-3">
              <span className="text-[#e67e22] font-semibold text-sm">Step-over</span>
            </div>
          </div>
          <div className="ml-13 bg-[#1a1a1a] border border-[#3a3a3a] rounded-sm p-4 mt-3">
            <p className="text-[#d6d6d6] font-semibold mb-2">If you use CAM:</p>
            <p className="text-sm text-[#a3a3a3]">Export directly for <span className="text-[#e67e22]">Aspire, Fusion 360, Carbide Create</span> (and others you've enabled).</p>
          </div>
          <div className="ml-13 bg-[#1a1a1a] border border-[#f39c12]/30 rounded-sm p-4 mt-3">
            <p className="text-[#f39c12] font-semibold mb-2">The calculator will flag:</p>
            <ul className="space-y-1 text-sm text-[#a3a3a3]">
              <li>• Conservative / Balanced / Aggressive cuts</li>
              <li>• Potential burn risk or chatter risk</li>
            </ul>
            <p className="text-[#d6d6d6] mt-2 text-sm font-medium">You stay in control, but you're not guessing.</p>
          </div>
        </div>

        {/* Step 6 - Troubleshooting */}
        <div className="bg-[#242424] border border-[#3a3a3a] rounded-sm p-5 space-y-4 hover:border-[#e67e22]/30 transition-colors">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#e67e22] rounded-full flex items-center justify-center text-white font-bold text-lg">6</div>
            <h3 className="text-lg md:text-xl font-bold text-white">Fix Problems Fast (Troubleshooting)</h3>
          </div>
          <p className="text-[#a3a3a3] ml-13">If the cut doesn't look, sound, or measure right:</p>
          <ul className="ml-13 space-y-2">
            <li className="flex items-start gap-2 text-[#d6d6d6]">
              <ChevronRight className="w-4 h-4 text-[#e67e22] mt-0.5" />
              Go to <Link to="/troubleshooting" className="text-[#e67e22] font-semibold hover:underline">Troubleshooting</Link>
            </li>
            <li className="flex items-start gap-2 text-[#d6d6d6]">
              <ChevronRight className="w-4 h-4 text-[#e67e22] mt-0.5" />
              Describe what you see: burn marks, chatter, fuzzy edges, wrong size, etc.
            </li>
            <li className="flex items-start gap-2 text-[#d6d6d6]">
              <ChevronRight className="w-4 h-4 text-[#e67e22] mt-0.5" />
              Select the material and tool you used
            </li>
          </ul>
          <div className="ml-13 bg-[#1a1a1a] border border-[#3a3a3a] rounded-sm p-4 mt-3">
            <p className="text-[#d6d6d6] font-semibold mb-2">You'll get targeted suggestions like:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <div className="text-[#a3a3a3]">"Slow feed / increase RPM"</div>
              <div className="text-[#a3a3a3]">"Reduce depth per pass"</div>
              <div className="text-[#a3a3a3]">"Change bit type"</div>
              <div className="text-[#a3a3a3]">"Improve clamping / workholding"</div>
            </div>
          </div>
        </div>

      </div>

      {/* Your New CNC Routine */}
      <div className="bg-[#2c2c2c]/80 border-2 border-[#e67e22]/50 rounded-sm p-6 space-y-4">
        <h2 className="text-2xl md:text-3xl font-bold text-white uppercase tracking-wide flex items-center gap-3" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          <RefreshCw className="w-7 h-7 text-[#e67e22]" />
          Your New CNC Routine
        </h2>
        <p className="text-[#d6d6d6]">Every job can follow the same simple loop:</p>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
          <div className="bg-[#1a1a1a] border border-[#3a3a3a] rounded-sm p-3 text-center">
            <div className="text-[#e67e22] font-bold text-lg">1</div>
            <div className="text-xs text-[#a3a3a3]">Check profile & equipment</div>
          </div>
          <div className="bg-[#1a1a1a] border border-[#3a3a3a] rounded-sm p-3 text-center">
            <div className="text-[#e67e22] font-bold text-lg">2</div>
            <div className="text-xs text-[#a3a3a3]">Pick tool + material</div>
          </div>
          <div className="bg-[#1a1a1a] border border-[#3a3a3a] rounded-sm p-3 text-center">
            <div className="text-[#e67e22] font-bold text-lg">3</div>
            <div className="text-xs text-[#a3a3a3]">Review & export</div>
          </div>
          <div className="bg-[#1a1a1a] border border-[#3a3a3a] rounded-sm p-3 text-center">
            <div className="text-[#e67e22] font-bold text-lg">4</div>
            <div className="text-xs text-[#a3a3a3]">Cut</div>
          </div>
          <div className="bg-[#1a1a1a] border border-[#e67e22]/30 rounded-sm p-3 text-center">
            <div className="text-[#e67e22] font-bold text-lg">5</div>
            <div className="text-xs text-[#a3a3a3]">If off → Troubleshoot</div>
          </div>
        </div>
        <p className="text-[#d6d6d6] text-center mt-4 text-lg font-medium">
          CNC-Helpdesk is built to make your routing <span className="text-[#e67e22]">safer</span>, <span className="text-[#e67e22]">faster</span>, and <span className="text-[#e67e22]">more predictable</span> – without turning you into an engineer.
        </p>
      </div>

      {/* CTA */}
      <div className="text-center pt-4">
        <Button
          onClick={handleProceed}
          className="bg-[#e67e22] hover:bg-[#d35400] text-white font-bold px-8 py-3 text-base uppercase tracking-wide shadow-[0_0_15px_rgba(230,126,34,0.4)] hover:shadow-[0_0_25px_rgba(230,126,34,0.5)]"
        >
          Get Started
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
      </div>
    </div>
  );
};

export default Introduction;
